import { useEffect, useState } from "react";
import { api, kioskApi } from "@/lib/api";
import { Bug } from "lucide-react";

const SEV_COLOR = {
  critical: "#FF2E63",
  high:     "#FF6B35",
  medium:   "#FFC542",
  low:      "#00F0FF",
  unknown:  "#8E9196",
};

/**
 * CVETicker — Bloomberg-style bottom band showing recent high-severity CVEs
 * from CIRCL. Works in both authenticated and kiosk contexts.
 *
 * Props:
 *  - kioskToken (optional) — if provided, uses the unauth `/api/cve-radar/kiosk-latest`
 *    endpoint with that token; otherwise uses the authenticated `/api/cve-radar/latest`.
 *  - minCvss (default 7)
 *  - sinceHours (default 168 = 1 week)
 */
export default function CVETicker({ kioskToken, minCvss = 7, sinceHours = 168 }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        const url = kioskToken
          ? `/cve-radar/kiosk-latest?limit=25&min_cvss=${minCvss}&since_hours=${sinceHours}&token=${encodeURIComponent(kioskToken)}`
          : `/cve-radar/latest?limit=25&min_cvss=${minCvss}&since_hours=${sinceHours}`;
        const client = kioskToken ? kioskApi : api;
        const r = await client.get(url);
        if (!ignore) setItems(r.data.items || []);
      } catch {
        if (!ignore) setItems([]);
      }
    }
    load();
    const t = setInterval(load, 5 * 60 * 1000);   // refresh every 5 min
    return () => { ignore = true; clearInterval(t); };
  }, [kioskToken, minCvss, sinceHours]);

  if (!items.length) return null;

  // Duplicate the item list so the marquee never leaves a gap.
  const marquee = [...items, ...items];

  return (
    <div
      className="w-full bg-black/70 backdrop-blur border-t border-white/10 overflow-hidden"
      data-testid="cve-ticker"
    >
      <div className="flex items-center gap-3">
        <div className="shrink-0 flex items-center gap-2 px-4 py-2 border-r border-white/10 bg-black/40">
          <Bug className="w-3.5 h-3.5 text-[#FF6B35]" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
            CVE Radar · CIRCL
          </span>
          <span className="text-[10px] font-mono text-[#FF6B35]">
            CVSS ≥ {minCvss}
          </span>
        </div>
        <div className="relative flex-1 overflow-hidden">
          <div
            className="flex items-center gap-8 whitespace-nowrap py-2"
            style={{ animation: "fq-marquee 80s linear infinite" }}
          >
            {marquee.map((c, i) => (
              <a
                key={`${c.cve_id}-${i}`}
                href={c.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-xs font-mono hover:text-white transition-colors"
              >
                <span
                  className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-widest"
                  style={{ background: `${SEV_COLOR[c.severity]}22`, color: SEV_COLOR[c.severity], border: `1px solid ${SEV_COLOR[c.severity]}55` }}
                >
                  {c.cvss != null ? `CVSS ${c.cvss.toFixed(1)}` : c.severity.toUpperCase()}
                </span>
                <span style={{ color: SEV_COLOR[c.severity] }}>{c.cve_id}</span>
                {c.matched_customers && c.matched_customers.length > 0 && (
                  <span
                    className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-widest bg-[#B57BFF]/20 text-[#B57BFF] border border-[#B57BFF]/60 flex items-center gap-1"
                    title={c.matched_customers.map(m => `${m.customer_name}: ${m.matched_terms.join(', ')}`).join(' · ')}
                  >
                    🎯 {c.matched_customers.length === 1
                      ? c.matched_customers[0].customer_name
                      : `${c.matched_customers.length} customers`}
                  </span>
                )}
                <span className="text-white/70 max-w-[560px] truncate">{c.summary}</span>
                {c.products && c.products.length > 0 && (
                  <span className="text-[#52525B]">· {c.products.slice(0, 2).join(", ")}</span>
                )}
              </a>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fq-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
